import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, fmtIST, errDetail } from "@/lib/api";
import { useUrlState } from "@/hooks/useUrlState";
import { buildReturnTo } from "@/hooks/useBackNavigation";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common";
import {
  BookOpen, Search, ExternalLink, FileText, Clock, ShieldCheck, GraduationCap,
  ScrollText, MessagesSquare, ClipboardList, Sparkles, Video, Youtube,
} from "lucide-react";

/* The three content types an item can be, all served from the same
   /knowledge/documents endpoint and discriminated by `content_type`. Each
   gets a visually DISTINCT icon and badge so an employee can tell a written
   SOP from an uploaded training video from a YouTube link at a glance in the
   grid, without opening anything. `filterLabel` drives the content-type
   filter below; `null` is the "any type" option. */
const CONTENT_TYPES = {
  document: {
    label: "Document", icon: FileText,
    tone: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  },
  video: {
    label: "Video", icon: Video,
    tone: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  },
  youtube: {
    label: "YouTube", icon: Youtube,
    tone: "bg-red-500/15 text-red-600 dark:text-red-400",
  },
};
const CONTENT_TYPE_FILTERS = [
  { value: "", label: "Any type" },
  { value: "document", label: "Documents" },
  { value: "video", label: "Uploaded videos" },
  { value: "youtube", label: "YouTube" },
];

const fmtDuration = (secs) => {
  if (!secs || !Number.isFinite(secs)) return null;
  const s = Math.floor(secs % 60);
  const m = Math.floor((secs / 60) % 60);
  const h = Math.floor(secs / 3600);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
};

/* Quick sections: "All" and "Recently Updated" are pure sort/no-filter
   views; the rest map straight onto the existing category filter — no new
   backend concept, just a faster way in for the categories staff reach for
   most. */
const QUICK_SECTIONS = [
  { key: "all", label: "All", icon: BookOpen, category: null },
  { key: "recent", label: "Recently Updated", icon: Clock, category: null },
  { key: "Policy", label: "Policies", icon: ShieldCheck, category: "Policy" },
  { key: "SOP", label: "SOPs", icon: ClipboardList, category: "SOP" },
  { key: "Training", label: "Training", icon: GraduationCap, category: "Training" },
  { key: "Sales Script", label: "Sales Scripts", icon: MessagesSquare, category: "Sales Script" },
  { key: "Compliance", label: "Compliance", icon: ScrollText, category: "Compliance" },
];

const CATEGORY_TONE = {
  SOP: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  Framework: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  Policy: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]",
  Training: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  "Sales Script": "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  Compliance: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
  Other: "bg-muted text-muted-foreground",
};

const RECENT_WINDOW_DAYS = 14;

const DocCard = ({ doc, products, returnTo }) => {
  const productName = products.find((p) => p.code === doc.product_code)?.name || doc.product_code;
  const type = CONTENT_TYPES[doc.content_type] || CONTENT_TYPES.document;
  const TypeIcon = type.icon;
  const duration = fmtDuration(doc.duration_seconds);
  return (
    <Card data-testid={`kb-doc-card-${doc.document_id}`} className="flex flex-col">
      <CardContent className="pt-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <TypeIcon className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5"
            data-testid={`kb-doc-type-icon-${doc.content_type || "document"}`} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-heading font-semibold leading-snug break-words">{doc.title}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <Badge variant="outline" className={`border-0 text-[10px] ${CATEGORY_TONE[doc.category] || CATEGORY_TONE.Other}`}>
                {doc.category}
              </Badge>
              {/* A distinct type badge for BOTH video kinds — an employee
                  choosing what to open cares whether it's a 40-minute
                  in-house recording or a YouTube link. Documents are the
                  unmarked default (the icon already says so) to keep the
                  badge row from becoming noise on the most common case. */}
              {doc.content_type !== "document" && (
                <Badge variant="outline" className={`border-0 text-[10px] ${type.tone}`}
                  data-testid={`kb-doc-type-badge-${doc.content_type}`}>
                  {type.label}
                </Badge>
              )}
              {/* Duration is opportunistic — see KB_VIDEO_STREAMING_AUDIT.md:
                  nothing in the current architecture derives one, so its
                  absence is the normal case and must never break the card. */}
              {duration && (
                <Badge variant="outline" className="text-[10px] tabular-nums"
                  data-testid={`kb-doc-duration-${doc.document_id}`}>
                  {duration}
                </Badge>
              )}
              {productName && <Badge variant="outline" className="text-[10px]">{productName}</Badge>}
              {doc.department && <Badge variant="outline" className="text-[10px]">{doc.department}</Badge>}
            </div>
          </div>
        </div>
        {doc.description && (
          <p className="text-xs text-muted-foreground line-clamp-3">{doc.description}</p>
        )}
        <div className="mt-auto pt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground border-t">
          <div className="min-w-0">
            <p className="truncate">Updated {fmtIST(doc.updated_at, { year: true, time: false })}</p>
            {doc.uploaded_by_name && <p className="truncate">by {doc.uploaded_by_name}</p>}
          </div>
          {/* Same-tab navigation with a return_to capturing this list's own
              current filter/section/search state (already URL-backed via
              useUrlState below) — the viewer's own Back control restores
              it exactly. A separately opened new tab (browser "open in new
              tab", copied URL, bookmark) has no return_to and no app
              history, so it safely falls back to a fresh Knowledge Base
              list instead (see useBackNavigation). */}
          <Link to={`/knowledge-base/view/${doc.document_id}?return_to=${returnTo}`} className="shrink-0">
            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`kb-doc-open-${doc.document_id}`}>
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default function KnowledgeBasePage() {
  const [docs, setDocs] = useState(null);
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  // URL-backed list state (shared Back-navigation foundation, 2026-09) —
  // section/search/product/department all round-trip through the URL, so
  // a document opened from a filtered view and returned from via Back
  // (or a plain page refresh) shows the exact same filtered list, never a
  // reset to defaults.
  // `type` (content-type filter) is an ADDITIVE key on the existing shared
  // useUrlState list state — not a replacement for it — so it round-trips
  // through the URL and is restored by the viewer's Back control exactly
  // like section/search/product/department already were.
  const [{ section, q, product, department, type }, setListState] = useUrlState({
    section: "all", q: "", product: "", department: "", type: "",
  });
  const routerLocation = useLocation();
  const returnTo = buildReturnTo(routerLocation);

  const activeCategory = QUICK_SECTIONS.find((s) => s.key === section)?.category || null;

  const load = useCallback(() => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (activeCategory) params.category = activeCategory;
    if (product) params.product_code = product;
    if (department) params.department = department;
    // Filtered server-side (a Mongo query on content_type), not by discarding
    // rows the client already fetched — so it composes correctly with search
    // and the other filters.
    if (type) params.content_type = type;
    setDocs(null);
    api.get("/knowledge/documents", { params })
      .then((r) => setDocs(r.data || []))
      .catch((e) => { setDocs([]); toast.error(errDetail(e, "Could not load the Knowledge Base")); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, activeCategory, product, department, type]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    // Departments filter is populated from the documents themselves as they
    // load below (see the `departments` derived list), so employees only
    // ever see department options that a real document is actually
    // labelled with — never a stale/typo'd value from elsewhere.
  }, []);

  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  useEffect(() => {
    if (!docs) return;
    const found = Array.from(new Set(docs.map((d) => d.department).filter(Boolean))).sort();
    setDepartments((prev) => Array.from(new Set([...prev, ...found])).sort());
  }, [docs]);

  const visibleDocs = useMemo(() => {
    if (!docs) return null;
    if (section !== "recent") return docs;
    const cutoff = Date.now() - RECENT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return [...docs]
      .filter((d) => d.updated_at && new Date(d.updated_at).getTime() >= cutoff)
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  }, [docs, section]);

  return (
    <div className="space-y-4" data-testid="knowledge-base-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
          <BookOpen className="h-6 w-6" />Knowledge Base
        </h1>
        <p className="text-sm text-muted-foreground">
          Company SOPs, policies, frameworks, training material and sales scripts — published by
          Administration and kept up to date here.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="kb-quick-sections">
        {QUICK_SECTIONS.map((s) => (
          <Button
            key={s.key}
            size="sm"
            variant={section === s.key ? "default" : "outline"}
            className="h-8 text-xs"
            data-testid={`kb-section-${s.key.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setListState({ section: s.key })}
          >
            <s.icon className="h-3.5 w-3.5 mr-1.5" />{s.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="kb-search-input"
              className="h-9 pl-8 text-xs"
              placeholder="Search title or description…"
              value={q}
              onChange={(e) => setListState({ q: e.target.value })}
            />
          </div>
          <Select value={product || "__any"} onValueChange={(v) => setListState({ product: v === "__any" ? "" : v })}>
            <SelectTrigger data-testid="kb-product-filter" className="h-9 w-44 text-xs"><SelectValue placeholder="Any product" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any">Any product</SelectItem>
              {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={department || "__any"} onValueChange={(v) => setListState({ department: v === "__any" ? "" : v })}>
            <SelectTrigger data-testid="kb-department-filter" className="h-9 w-44 text-xs"><SelectValue placeholder="Any department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any">Any department</SelectItem>
              {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type || "__any"} onValueChange={(v) => setListState({ type: v === "__any" ? "" : v })}>
            <SelectTrigger data-testid="kb-type-filter" className="h-9 w-44 text-xs"><SelectValue placeholder="Any type" /></SelectTrigger>
            <SelectContent>
              {CONTENT_TYPE_FILTERS.map((t) => (
                <SelectItem key={t.value || "__any"} value={t.value || "__any"}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {visibleDocs === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-36" />)}
        </div>
      ) : visibleDocs.length === 0 ? (
        <EmptyState
          testId="kb-empty-state"
          icon={Sparkles}
          title="No documents match yet"
          hint={q || product || department || type || activeCategory
            ? "Try clearing a filter or search term."
            : "Nothing has been published to the Knowledge Base yet — check back soon."}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kb-doc-grid">
          {visibleDocs.map((d) => <DocCard key={d.document_id} doc={d} products={products} returnTo={returnTo} />)}
        </div>
      )}
    </div>
  );
}
